import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "../../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  Timestamp,
  collection,
  getDocs,
} from "firebase/firestore";
import {
  Card,
  Form,
  Button,
  Alert,
  Table,
  Spinner,
  Row,
  Col,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import CommunityMaintenanceEditor from "./CommunityMaintenanceEditor";

/* --------------------------- Helpers & Builders --------------------------- */

const makeId = (prefix = "BLOCK") =>
  `${prefix}-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

const pad = (n, width) => String(n).padStart(width, "0");

/**
 * Derive numbering pattern from user "start number".
 * Examples:
 *  - 101   -> floorBase=1, indexStart=1, indexWidth=2  => A-101, A-102...
 *  - 1102  -> floorBase=11, indexStart=2, indexWidth=2 => B-1102, B-1103...
 * Rules:
 *  - If digits <= 2: treat as indexStart; floorBase defaults to 1; indexWidth = max(2, len)
 *  - If digits >= 3: last 2 digits = indexStart; rest = floorBase; indexWidth=2
 */
const deriveFromStartNumber = (startNumberInput) => {
  const s = String(startNumberInput || "").replace(/\D/g, "");
  if (!s) {
    return {
      floorBase: 1,
      indexStart: 1,
      indexWidth: 2,
      startLabel: "1" + pad(1, 2),
    };
  }
  if (s.length <= 2) {
    const indexStart = parseInt(s, 10);
    const indexWidth = Math.max(2, s.length);
    return {
      floorBase: 1,
      indexStart,
      indexWidth,
      startLabel: `1${pad(indexStart, indexWidth)}`,
    };
  }
  const floorBase = parseInt(s.slice(0, -2), 10);
  const indexStart = parseInt(s.slice(-2), 10);
  const indexWidth = 2;
  return {
    floorBase,
    indexStart,
    indexWidth,
    startLabel: `${floorBase}${pad(indexStart, indexWidth)}`,
  };
};

/**
 * Compute the list of floor labels to use, based on config:
 * - If customFloorLabelsEnabled: use provided labels (trimmed), fallback to default numeric code per floor
 * - Else: use default numeric codes starting from floorBase
 */
const getFloorLabels = (cfg) => {
  const { floors, customFloorLabelsEnabled, customFloorLabels, startNumberInput } = cfg;
  const { floorBase } = deriveFromStartNumber(startNumberInput);

  const defaults = Array.from({ length: Number(floors) || 0 }, (_, i) =>
    String(floorBase + i)
  );

  if (!customFloorLabelsEnabled) return defaults;

  const provided = Array.from({ length: Number(floors) || 0 }, (_, i) => {
    const raw = (customFloorLabels || [])[i];
    const trimmed = String(raw || "").trim();
    return trimmed || defaults[i];
  });

  return provided;
};

/**
 * Build preview rows and complete flat labels for one block:
 * - Ground (optional): BLOCK-Gxx with same index range/width and count = flatsPerFloor
 * - Floor rows: BLOCK-<floorLabel><idx> (floorLabel from custom labels or numeric)
 */
const buildRowsForBlock = (cfg) => {
  const {
    name,
    floors,
    flatsPerFloor,
    hasGround,
    groundLabel,
    startNumberInput,
    customFloorLabelsEnabled,
    customFloorLabels,
  } = cfg;

  const { floorBase, indexStart, indexWidth, startLabel } =
    deriveFromStartNumber(startNumberInput);

  const rows = [];
  const floorLabels = getFloorLabels(cfg); // respects custom labels

  // Ground row first (if enabled)
  if (hasGround && flatsPerFloor > 0) {
    const g = [];
    for (let i = 0; i < flatsPerFloor; i++) {
      const idx = indexStart + i;
      g.push(`${name}-${groundLabel}${pad(idx, indexWidth)}`);
    }
    rows.push({
      key: `G-${name}`,
      label: `Ground (${groundLabel})`,
      flats: g,
      floorNumber: 0,
    });
  }

  // Floors 1..N with floorLabels
  for (let f = 1; f <= floors; f++) {
    const floorLabel = floorLabels[f - 1]; // "11", "12" or "L1", "PH", ...
    const row = [];
    for (let i = 0; i < flatsPerFloor; i++) {
      const idx = indexStart + i;
      row.push(`${name}-${floorLabel}${pad(idx, indexWidth)}`);
    }
    rows.push({
      key: `F${f}-${name}`,
      label: `Floor ${f}`,
      flats: row,
      floorNumber: f,
    });
  }

  // Start/end labels
  const effectiveStartLabel =
    floorLabels.length > 0
      ? `${name}-${floorLabels[0]}${pad(indexStart, indexWidth)}`
      : `${name}-${startLabel}`;
  const effectiveEndLabel =
    floorLabels.length > 0
      ? `${name}-${floorLabels[floorLabels.length - 1]}${pad(
          indexStart + flatsPerFloor - 1,
          indexWidth
        )}`
      : `${name}-${floorBase + floors - 1}${pad(
          indexStart + flatsPerFloor - 1,
          indexWidth
        )}`;

  const allFlats = rows.flatMap((r) => r.flats);

  return {
    rows,
    startNumberLabel: effectiveStartLabel,
    endNumberLabel: effectiveEndLabel,
    allFlats,
  };
};

/* ----------------------------- Main Component ---------------------------- */

export default function AddBlock({ communityId: communityIdProp }) {
  // If not provided as prop, read from /manager/:communityId
  const { communityId: fromRoute } = useParams();
  const communityId = communityIdProp || fromRoute;

  // Show/hide configuration section
  const [showConfig, setShowConfig] = useState(false);

  // Persisted community blocks + residents
  const [loading, setLoading] = useState(true);
  const [savedBlocks, setSavedBlocks] = useState([]); // from Firestore
  const [residentsMap, setResidentsMap] = useState({}); // { "A-101": "Name", ... }

  // Per-block configs (independent)
  const [blocks, setBlocks] = useState([]); // empty until user opens config

  // Preview & status
  const [previewBlocks, setPreviewBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // Edit mode for persisted block (replace by name)
  const [editMode, setEditMode] = useState(false);
  const [editingBlockName, setEditingBlockName] = useState("");

  const [selectedMaintenanceBlock, setSelectedMaintenanceBlock] =
  useState(null);

const [showMaintenanceModal, setShowMaintenanceModal] =
  useState(false);

  /* -------------------------- Initial data loading -------------------------- */

  useEffect(() => {
    const load = async () => {
      if (!communityId) {
        setError("Missing communityId.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const ref = doc(db, "communities", communityId);
        const snap = await getDoc(ref);
        if (!snap.exists()) throw new Error("Community not found.");

        const data = snap.data() || {};
        const persisted = Array.isArray(data.blocks) ? data.blocks : [];
        setSavedBlocks(persisted);

        // Residents from subcollection (preferred)
        const subRef = collection(db, "communities", communityId, "residents");
        const subSnap = await getDocs(subRef);
        const fromSub = {};
        subSnap.forEach((d) => {
          const r = d.data() || {};
          if (r.flat && r.name) fromSub[String(r.flat)] = String(r.name);
        });

        // Residents from map on doc (fallback)
        const fromMap =
          data.residentsMap && typeof data.residentsMap === "object"
            ? data.residentsMap
            : {};

        setResidentsMap({ ...fromMap, ...fromSub });
      } catch (err) {
        console.error("Initial load error:", err);
        setError(err?.message || "Failed to load community.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [communityId]);

  /* ------------------------------- Utilities ------------------------------- */

  const normalizeName = (s) => String(s || "").trim().toLowerCase();

  const hasDuplicateNamesInForm = () => {
    const seen = new Set();
    for (const b of blocks) {
      const nm = normalizeName(b.name);
      if (!nm) continue;
      if (seen.has(nm)) return true;
      seen.add(nm);
    }
    return false;
  };

  const existsInSaved = (name) => {
    const nm = normalizeName(name);
    return savedBlocks.some((b) => normalizeName(b.name) === nm);
  };

  const computeDashboard = useMemo(() => {
    const totalBlocks = savedBlocks.length;
    const totalFloors = savedBlocks.reduce(
      (acc, b) => acc + Number(b.floors || 0),
      0
    );
    const avgFloorsPerBlock =
      totalBlocks > 0 ? (totalFloors / totalBlocks).toFixed(1) : "0.0";

    const totalFlats = savedBlocks.reduce((acc, b) => {
      const fpf = Number(b.flatsPerFloor || 0);
      const floors = Number(b.floors || 0);
      const groundAdd = b.hasGround ? 1 : 0;
      return acc + fpf * (floors + groundAdd);
    }, 0);

    return {
      totalBlocks,
      totalFloors,
      totalFlats,
      avgFloorsPerBlock,
    };
  }, [savedBlocks]);

  /* ------------------------------ Form actions ----------------------------- */

  const defaultRow = () => ({
    id: makeId("CFG"),
    name: "",
    floors: 3,
    flatsPerFloor: 3,
    startNumberInput: "101",
    hasGround: false,
    groundLabel: "G",
    customFloorLabelsEnabled: false,
    customFloorLabels: [], // length will match floors when enabled
  });

  const addBlockRow = () => {
    setBlocks((prev) => [...prev, defaultRow()]);
  };

  const removeBlockRow = (id) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  };

  const updateBlock = (id, patch) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const syncCustomLabelsLength = (cfg, nextFloors) => {
    // ensure customFloorLabels length equals nextFloors
    const { floorBase } = deriveFromStartNumber(cfg.startNumberInput);
    const defaults = Array.from({ length: nextFloors }, (_, i) =>
      String(floorBase + i)
    );

    const prev = Array.isArray(cfg.customFloorLabels)
      ? cfg.customFloorLabels
      : [];

    const next = Array.from({ length: nextFloors }, (_, i) => prev[i] || "");
    return next;
  };

  const startAddFlow = () => {
    setError("");
    setInfo("");
    setEditMode(false);
    setEditingBlockName("");
    setShowConfig(true);
    setBlocks([]);
    setPreviewBlocks([]);
    // add one empty row by default
    setTimeout(() => addBlockRow(), 0);
  };

  const cancelConfig = () => {
    setShowConfig(false);
    setBlocks([]);
    setPreviewBlocks([]);
    setEditMode(false);
    setEditingBlockName("");
    setError("");
    setInfo("");
  };

  const openMaintenanceEditor = (block) => {
  setSelectedMaintenanceBlock(block);
  setShowMaintenanceModal(true);
  setError("");
  setInfo("");
};

const closeMaintenanceEditor = () => {
  setShowMaintenanceModal(false);
  setSelectedMaintenanceBlock(null);
};

  // Begin editing an existing (persisted) block by name
  const beginEditSavedBlock = (blk) => {
    setError("");
    setInfo("");
    setEditMode(true);
    setEditingBlockName(blk.name);
    setShowConfig(true);
    setPreviewBlocks([]);

    const rowCfg = {
      ...defaultRow(),
      id: blk.id || makeId("BLOCK"),
      name: blk.name,
      floors: Number(blk.floors) || 1,
      flatsPerFloor: Number(blk.flatsPerFloor) || 1,
      startNumberInput:
        String((blk.startNumber || "").split("-")[1] || "101").replace(/\D/g, "") ||
        "101",
      hasGround: !!blk.hasGround,
      groundLabel: blk.groundLabel || "G",
      customFloorLabelsEnabled: !!blk.customFloorLabelsEnabled,
      customFloorLabels:
        Array.isArray(blk.customFloorLabels) ? blk.customFloorLabels : [],
    };
    // make sure length matches floors
    rowCfg.customFloorLabels = syncCustomLabelsLength(rowCfg, rowCfg.floors);
    setBlocks([rowCfg]);
  };

  const deleteSavedBlock = async (blk) => {
    if (!communityId) {
      setError("Missing communityId.");
      return;
    }
    if (!window.confirm(`Delete block "${blk.name}"? Apartment maintenance records will not be automatically deleted.`)) return;
    try {
      setSaving(true);
      const ref = doc(db, "communities", communityId);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error("Community not found.");

      const data = snap.data() || {};
      const existing = Array.isArray(data.blocks) ? data.blocks : [];
      const filtered = existing.filter(
        (b) => normalizeName(b.name) !== normalizeName(blk.name)
      );
      await updateDoc(ref, { blocks: filtered });

      setSavedBlocks(filtered);
      setInfo(`Deleted block "${blk.name}".`);
      setError("");
    } catch (err) {
      console.error("deleteSavedBlock error:", err);
      setError(err?.message || "Failed to delete block.");
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------- Validation ------------------------------ */

  const validate = () => {
    if (!communityId) return "Missing communityId.";
    if (blocks.length === 0) return "Add at least one block.";

    for (const b of blocks) {
      if (!String(b.name || "").trim())
        return "Each block requires a name.";
      if (Number(b.floors) <= 0) return `Block ${b.name || ""}: floors must be > 0`;
      if (Number(b.flatsPerFloor) <= 0)
        return `Block ${b.name || ""}: flats per floor must be > 0`;
      if (!String(b.startNumberInput || "").trim())
        return `Block ${b.name || ""}: start number is required (e.g., 101)`;
      if (!/^\d+$/.test(String(b.startNumberInput)))
        return `Block ${b.name || ""}: start number must be digits only`;
      if (b.hasGround && !String(b.groundLabel || "").trim())
        return `Block ${b.name || ""}: ground label is required (e.g., G)`;

      // If custom labels enabled, ensure none are empty
      if (b.customFloorLabelsEnabled) {
        const labels = getFloorLabels(b);
        const anyEmpty = labels.some((l) => !String(l || "").trim());
        if (anyEmpty)
          return `Block ${b.name || ""}: all custom floor labels must be filled.`;
      }
    }

    if (hasDuplicateNamesInForm())
      return "Duplicate block names in form. Each block name must be unique (case-insensitive).";

    for (const b of blocks) {
      const formName = b.name;
      if (editMode && normalizeName(formName) === normalizeName(editingBlockName)) {
        continue; // editing same block name allowed
      }
      if (existsInSaved(formName)) {
        return `Block name "${formName}" already exists. Please choose a different name.`;
      }
    }

    return "";
  };

  /* ----------------------------- Preview & Save ---------------------------- */

  const generatePreview = () => {
    setError("");
    setInfo("");

    const err = validate();
    if (err) return setError(err);

    // Replace preview fully (no accumulation)
    const blocksPreview = blocks.map((cfg) => {
      const { rows, startNumberLabel, endNumberLabel, allFlats } =
        buildRowsForBlock(cfg);

      return {
        id: cfg.id || makeId("BLOCK"),
        name: cfg.name.trim(),
        floors: Number(cfg.floors),
        flatsPerFloor: Number(cfg.flatsPerFloor),
        hasGround: !!cfg.hasGround,
        groundLabel: cfg.hasGround ? cfg.groundLabel : "",
        startNumberInput: String(cfg.startNumberInput),
        startNumber: startNumberLabel,
        endNumber: endNumberLabel,
        flats: allFlats,
        customFloorLabelsEnabled: !!cfg.customFloorLabelsEnabled,
        customFloorLabels: cfg.customFloorLabelsEnabled ? getFloorLabels(cfg) : [],
        _rows: rows, // preview-only; stripped before save
        createdAt: Timestamp.now(), // concrete value allowed inside arrays
      };
    });

    setPreviewBlocks(blocksPreview);
    setInfo("Preview generated. Review below and click Confirm to save.");
  };

  const confirmSave = async () => {
    setError("");
    setInfo("");

    if (!communityId) return setError("Missing communityId.");
    if (previewBlocks.length === 0) return setError("Generate preview first.");

    try {
      setSaving(true);

      const ref = doc(db, "communities", communityId);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error("Community not found.");

      const data = snap.data() || {};
      const existing = Array.isArray(data.blocks) ? data.blocks : [];

      const finalNamesLower = new Set(
        existing.map((b) => normalizeName(b.name))
      );

      // Strip preview-only fields, keep Timestamp.now(), persist custom labels
      const toPersist = previewBlocks.map(({ _rows, ...rest }) => rest);

      let newBlocks;
      if (editMode && editingBlockName) {
        const target = normalizeName(editingBlockName);
        const filtered = existing.filter(
          (b) => normalizeName(b.name) !== target
        );

        // prevent collision if name changed
        for (const bp of toPersist) {
          const nm = normalizeName(bp.name);
          if (nm !== target && filtered.some((b) => normalizeName(b.name) === nm)) {
            throw new Error(
              `Block name "${bp.name}" already exists. Cannot update.`
            );
          }
        }
        newBlocks = [...filtered, ...toPersist];
      } else {
        for (const bp of toPersist) {
          const nm = normalizeName(bp.name);
          if (finalNamesLower.has(nm)) {
            throw new Error(
              `Block name "${bp.name}" already exists. Cannot add duplicate.`
            );
          }
        }
        newBlocks = [...existing, ...toPersist];
      }

      await updateDoc(ref, { blocks: newBlocks });

      setSavedBlocks(newBlocks);
      setInfo(editMode ? "Block updated successfully." : "Blocks saved successfully.");
      setPreviewBlocks([]);

      // Hide configuration section after save
      setShowConfig(false);
      setBlocks([]);
      setEditMode(false);
      setEditingBlockName("");
    } catch (err) {
      console.error("confirmSave error:", err?.code, err?.message || err);
      setError(err?.message || "Failed to save blocks.");
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------ Render helpers --------------------------- */

  const renderResidentTooltip = (flatLabel) => {
    const resident = residentsMap[flatLabel];
    const isAssigned = !!resident;
    return (
      <Tooltip id={`tt-${flatLabel}`}>
        <div className="d-flex align-items-center gap-2">
          <Badge bg={isAssigned ? "info" : "secondary"}>
            {isAssigned ? "Registered" : "Unassigned"}
          </Badge>
          <div style={{ whiteSpace: "nowrap" }}>
            {isAssigned ? resident : "No resident"}
          </div>
        </div>
      </Tooltip>
    );
  };

  const renderFlatPill = (flatLabel) => {
    const resident = residentsMap[flatLabel];
    const isAssigned = !!resident;

    return (
      <OverlayTrigger
        key={`ot-${flatLabel}`}
        placement="top"
        overlay={renderResidentTooltip(flatLabel)}
        delay={{ show: 200, hide: 100 }}
      >
        <span
          className="px-2 py-1 rounded bg-light border d-inline-flex align-items-center gap-2"
          style={{ cursor: "help" }}
        >
          <code className="m-0">{flatLabel}</code>
          <Badge bg={isAssigned ? "success" : "secondary"}>
            {isAssigned ? resident : "Unassigned"}
          </Badge>
        </span>
      </OverlayTrigger>
    );
  };

  const renderPreviewGrid = () => {
    if (previewBlocks.length === 0) return null;

    const maxFloors = Math.max(...previewBlocks.map((b) => Number(b.floors)));
    const showGround = previewBlocks.some((b) => b.hasGround);

    return (
      <Table bordered responsive className="mt-3">
        <thead>
          <tr>
            <th>Label</th>
            {previewBlocks.map((b) => (
              <th key={b.id}>
                <div><strong>Block name</strong></div>
                <div>{b.name}</div>
              </th>
            ))}
          </tr>
          <tr>
            <th>No of floors</th>
            {previewBlocks.map((b) => (
              <th key={`${b.id}-floors`}>{b.floors}</th>
            ))}
          </tr>
          <tr>
            <th>Flats per floor</th>
            {previewBlocks.map((b) => (
              <th key={`${b.id}-fpf`}>{b.flatsPerFloor}</th>
            ))}
          </tr>
          <tr>
            <th>Flat number start from</th>
            {previewBlocks.map((b) => (
              <th key={`${b.id}-start`}>{b.startNumber}</th>
            ))}
          </tr>
          <tr>
            <th>Flat number end</th>
            {previewBlocks.map((b) => (
              <th key={`${b.id}-end`}>{b.endNumber}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {showGround && (
            <tr>
              <td><strong>Flat No:</strong> Ground</td>
              {previewBlocks.map((b) => {
                const gRow = (b._rows || []).find((r) => r.floorNumber === 0);
                const flats = gRow?.flats || [];
                return (
                  <td key={`${b.id}-ground`}>
                    <div className="d-flex flex-wrap gap-2">
                      {flats.map((fn) => renderFlatPill(fn))}
                    </div>
                  </td>
                );
              })}
            </tr>
          )}
          {Array.from({ length: maxFloors }).map((_, i) => {
            const f = i + 1;
            return (
              <tr key={`floor-${f}`}>
                <td><strong>Flat No:</strong> Floor {f}</td>
                {previewBlocks.map((b) => {
                  const row = (b._rows || []).find(
                    (r) => r.floorNumber === f
                  );
                  const flats = row?.flats || [];
                  return (
                    <td key={`${b.id}-floor-${f}`}>
                      <div className="d-flex flex-wrap gap-2">
                        {flats.map((fn) => renderFlatPill(fn))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  /* --------------------------------- UI ----------------------------------- */

  return (
    <Card>
      <Card.Header>
        <strong>Blocks</strong>{" "}
        {communityId ? (
          <span className="text-muted">for {communityId}</span>
        ) : null}
      </Card.Header>

      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {info && <Alert variant="success">{info}</Alert>}

        {/* Dashboard cards */}
        <Row className="g-3 mb-3">
          <Col md={3}>
            <Card className="h-100">
              <Card.Body>
                <div className="text-muted">Total Blocks</div>
                <div className="display-6">{computeDashboard.totalBlocks}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100">
              <Card.Body>
                <div className="text-muted">Floors per Block (Avg)</div>
                <div className="display-6">{computeDashboard.avgFloorsPerBlock}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100">
              <Card.Body>
                <div className="text-muted">Total Floors</div>
                <div className="display-6">{computeDashboard.totalFloors}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100">
              <Card.Body>
                <div className="text-muted">Total Flats</div>
                <div className="display-6">{computeDashboard.totalFlats}</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Saved blocks list with actions */}
        <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span>Saved Blocks</span>
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={startAddFlow}
                disabled={loading || saving}
              >
                + Add Block
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" />
                <span>Loading blocks…</span>
              </div>
            ) : savedBlocks.length === 0 ? (
              <div className="text-muted">No blocks added yet.</div>
            ) : (
              <Table size="sm" striped hover responsive>
                <thead>
                  <tr>
                    <th>Block</th>
                    <th>Floors</th>
                    <th>Flats/floor</th>
                    <th>Start</th>
                    <th>End</th>
                    <th style={{ width: 360 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {savedBlocks.map((b) => (
                    <tr key={b.name}>
                      <td>{b.name}</td>
                      <td>{b.floors}</td>
                      <td>{b.flatsPerFloor}</td>
                      <td>{b.startNumber}</td>
                      <td>{b.endNumber}</td>
                      <td>
                       <div className="d-flex flex-wrap gap-2">
  <Button
    size="sm"
    variant="outline-primary"
    onClick={() => openMaintenanceEditor(b)}
    disabled={saving}
  >
    Apartment Details
  </Button>

  <Button
    size="sm"
    variant="outline-secondary"
    onClick={() => beginEditSavedBlock(b)}
    disabled={saving}
  >
    Edit Block
  </Button>

  <Button
    size="sm"
    variant="outline-danger"
    onClick={() => deleteSavedBlock(b)}
    disabled={saving}
  >
    Delete
  </Button>
</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <CommunityMaintenanceEditor
  show={showMaintenanceModal}
  onHide={closeMaintenanceEditor}
  communityId={communityId}
  block={selectedMaintenanceBlock}
/>
          </Card.Body>
          
        </Card>

        {/* Configuration section (hidden until Add Block / Edit) */}
        {showConfig && (
          <Card className="mb-3">
            <Card.Header>
              <strong>
                {editMode ? `Edit Block: ${editingBlockName}` : "Add Block(s)"}
              </strong>
            </Card.Header>

            <Card.Body>
              {blocks.length === 0 && (
                <div className="text-muted mb-2">
                  No block rows yet. Click “Add Row” to start.
                </div>
              )}

              <div className="mb-2 d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Block Configurations</h6>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={addBlockRow}
                  >
                    + Add Row
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={cancelConfig}>
                    Cancel
                  </Button>
                </div>
              </div>

              {blocks.map((b) => {
                const { floorBase } = deriveFromStartNumber(b.startNumberInput);
                const currentDefaults = Array.from({ length: Number(b.floors) || 0 }, (_, i) =>
                  String(floorBase + i)
                );

                return (
                  <Card key={b.id} className="mb-3">
                    <Card.Body>
                      <Row className="g-2">
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label>Block Name</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="e.g., A or Pruthvi"
                              value={b.name}
                              onChange={(e) =>
                                updateBlock(b.id, { name: e.target.value })
                              }
                              isInvalid={
                                !!b.name &&
                                ((existsInSaved(b.name) &&
                                  !(editMode &&
                                    normalizeName(b.name) ===
                                      normalizeName(editingBlockName))) ||
                                  blocks
                                    .filter((x) => x.id !== b.id)
                                    .some(
                                      (x) =>
                                        normalizeName(x.name) ===
                                        normalizeName(b.name)
                                    ))
                              }
                            />
                            <Form.Control.Feedback type="invalid">
                              Duplicate block name not allowed.
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label>No of floors</Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              value={b.floors}
                              onChange={(e) => {
                                const nextFloors = Number(e.target.value);
                                const nextLabels = b.customFloorLabelsEnabled
                                  ? syncCustomLabelsLength(b, nextFloors)
                                  : [];
                                updateBlock(b.id, {
                                  floors: nextFloors,
                                  customFloorLabels: nextLabels,
                                });
                              }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label>Flats per floor</Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              value={b.flatsPerFloor}
                              onChange={(e) =>
                                updateBlock(b.id, {
                                  flatsPerFloor: Number(e.target.value),
                                })
                              }
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Start number (digits)</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="e.g., 101 or 1102"
                              value={b.startNumberInput}
                              onChange={(e) =>
                                updateBlock(b.id, {
                                  startNumberInput: e.target.value,
                                })
                              }
                            />
                            <Form.Text muted>
                              Example: 101 → {(b.name || "X")}-101, 1102 →{" "}
                              {(b.name || "X")}-1102
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Ground floor</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                              <Form.Check
                                type="switch"
                                id={`has-ground-${b.id}`}
                                checked={!!b.hasGround}
                                onChange={(e) =>
                                  updateBlock(b.id, { hasGround: e.target.checked })
                                }
                                label={b.hasGround ? "Enabled" : "Disabled"}
                              />
                              {b.hasGround && (
                                <Form.Control
                                  type="text"
                                  placeholder="G"
                                  style={{ maxWidth: 80 }}
                                  value={b.groundLabel}
                                  onChange={(e) =>
                                    updateBlock(b.id, {
                                      groundLabel: e.target.value,
                                    })
                                  }
                                />
                              )}
                            </div>
                            <Form.Text muted>
                              Uses same flat count & numbering as floors (e.g.,{" "}
                              {b.name || "X"}-G01..).
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* Custom floor labels toggle */}
                      <Row className="g-2 mt-2">
                        <Col md={3}>
                          <Form.Group>
                            <Form.Check
                              type="switch"
                              id={`custom-labels-${b.id}`}
                              checked={!!b.customFloorLabelsEnabled}
                              onChange={(e) => {
                                const enabled = e.target.checked;
                                const nextLabels = enabled
                                  ? syncCustomLabelsLength(b, Number(b.floors) || 0)
                                  : [];
                                updateBlock(b.id, {
                                  customFloorLabelsEnabled: enabled,
                                  customFloorLabels: nextLabels,
                                });
                              }}
                              label={
                                b.customFloorLabelsEnabled
                                  ? "Custom floor labels: Enabled"
                                  : "Custom floor labels: Disabled"
                              }
                            />
                            <Form.Text muted>
                              When enabled, you can set your own floor codes (e.g.,
                              L1, L2, PH).
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* Custom floor labels inputs */}
                      {b.customFloorLabelsEnabled && (
                        <Row className="g-2 mt-1">
                          {Array.from({ length: Number(b.floors) || 0 }).map(
                            (_, i) => (
                              <Col md={2} key={`cfl-${b.id}-${i}`}>
                                <Form.Group>
                                  <Form.Label>Floor {i + 1} label</Form.Label>
                                  <Form.Control
                                    type="text"
                                    placeholder={`e.g., ${currentDefaults[i]} or L${i + 1}`}
                                    value={b.customFloorLabels[i] || ""}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      const next = [...b.customFloorLabels];
                                      next[i] = val;
                                      updateBlock(b.id, {
                                        customFloorLabels: next,
                                      });
                                    }}
                                  />
                                </Form.Group>
                              </Col>
                            )
                          )}
                        </Row>
                      )}

                      <div className="mt-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={generatePreview}
                        >
                          Update Preview
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="ms-2"
                          onClick={() => removeBlockRow(b.id)}
                        >
                          Remove Row
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                );
              })}

              {/* Actions */}
              <div className="mt-3 d-flex gap-2">
                <Button variant="primary" onClick={generatePreview}>
                  Generate Preview
                </Button>
                <Button
                  variant="success"
                  onClick={confirmSave}
                  disabled={saving || previewBlocks.length === 0}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" /> Saving…
                    </>
                  ) : editMode ? (
                    "Confirm & Update"
                  ) : (
                    "Confirm & Save"
                  )}
                </Button>
                <Button variant="outline-secondary" onClick={cancelConfig}>
                  Cancel
                </Button>
              </div>

              {/* Full-floor preview */}
              {renderPreviewGrid()}
            </Card.Body>
          </Card>
        )}
      </Card.Body>
    </Card>
  );
}
